import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  ArrowLeft, Star, Shield, MapPin, Phone,
  CheckCircle, ChevronRight, Stethoscope, UserRound, Home,
  Heart, MessageCircle, Award,
} from 'lucide-react'
import {
  providerService,
  Provider,
  ServiceRate,
  ProviderReview,
} from '@lib/supabase/provider.service'
import { useQuery } from '@tanstack/react-query'
import Button from '@components/web/Button'
import ProviderReviewsPanel from '@components/web/ProviderReviewsPanel'
import { clsx } from 'clsx'
import { usePatientAuth } from '@hooks/usePatientAuth'
import { queryKeys } from '@lib/queryKeys'

// ---- constants --------------------------------------------------------------
const DERMATOLOGY_ICON = '/dermatology.png'

const SERVICE_META: Record<string, { color: string; textColor: string; bg: string; icon: any }> = {
  'Doctor Visit':       { color: '#2563EB', textColor: 'text-blue-600',    bg: 'bg-blue-50',    icon: Stethoscope },
  'Nurse Care':         { color: '#059669', textColor: 'text-emerald-600', bg: 'bg-emerald-50', icon: UserRound },
  'Home Care':          { color: '#7C3AED', textColor: 'text-purple-600',  bg: 'bg-purple-50',  icon: Home },
  'Specialized Care':   { color: '#DC2626', textColor: 'text-rose-600',    bg: 'bg-rose-50',    icon: Heart },
  Dermatology:          { color: '#0F766E', textColor: 'text-teal-600',    bg: 'bg-teal-50',    icon: 'dermatology' },
  'Chat Consultation':  { color: '#0EA5E9', textColor: 'text-sky-600',     bg: 'bg-sky-50',     icon: MessageCircle },
}

function getInitials(name: string) {
  return name.trim().split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase()
}

function StarRow({ rating, count }: { rating: number; count: number }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex">
        {[1, 2, 3, 4, 5].map((s) => (
          <Star
            key={s}
            size={15}
            className={s <= Math.round(rating) ? 'text-yellow-400 fill-yellow-400' : 'text-muted-foreground/30'}
          />
        ))}
      </div>
      <span className="text-sm font-medium text-foreground">{rating.toFixed(1)}</span>
      <span className="text-sm text-muted-foreground">({count} reviews)</span>
    </div>
  )
}

// ---- main -------------------------------------------------------------------
export default function ProviderDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { patient } = usePatientAuth()

  const [showAllReviews, setShowAllReviews] = useState(false)

  const providerDetailQuery = useQuery({
    queryKey: queryKeys.patient.providerDetail(patient?.id, id),
    enabled: !!id,
    queryFn: async () => {
      const [provider, rates, ratingStats, reviews] = await Promise.all([
        providerService.getProviderById(id!),
        providerService.getProviderServiceRates(id!),
        providerService.getProviderRatingStats(id!),
        providerService.getProviderReviews(id!),
      ])

      return {
        provider,
        rates,
        reviews,
        avgRating: ratingStats.avgRating,
        totalReviews: ratingStats.totalReviews,
      }
    },
  })

  const provider: Provider | null = providerDetailQuery.data?.provider ?? null
  const rates: ServiceRate[] = providerDetailQuery.data?.rates ?? []
  const reviews: ProviderReview[] = providerDetailQuery.data?.reviews ?? []
  const avgRating = providerDetailQuery.data?.avgRating ?? 0
  const totalReviews = providerDetailQuery.data?.totalReviews ?? 0
  const isLoading = providerDetailQuery.isPending

  const activeServices = rates.filter((r) => r.is_active && r.base_rate !== null)
  const displayedReviews = showAllReviews ? reviews : reviews.slice(0, 3)

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 space-y-4 animate-pulse">
        <div className="h-8 w-24 bg-muted rounded" />
        <div className="h-40 bg-muted rounded-xl" />
        <div className="h-32 bg-muted rounded-xl" />
        <div className="h-48 bg-muted rounded-xl" />
      </div>
    )
  }

  if (!provider) {
    return (
      <div className="p-4 sm:p-6 text-center py-20">
        <p className="text-muted-foreground">Provider not found.</p>
        <Button className="mt-4" onClick={() => navigate('/patient/dashboard')}>Back to Dashboard</Button>
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Back */}
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
        <ArrowLeft size={16} /> Back
      </button>

      {/* Provider Header Card */}
      <div className="bg-card border border-border border p-6">
        <div className="flex items-start gap-5">
          {/* Avatar */}
          <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0 overflow-hidden">
            {provider.profile_image_url ? (
              <img src={provider.profile_image_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-primary font-bold text-2xl">{getInitials(provider.name)}</span>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold text-foreground">{provider.name}</h1>
              {provider.is_verified && (
                <span className="inline-flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full font-medium">
                  <CheckCircle size={12} fill="#059669" /> Verified
                </span>
              )}
            </div>
            <p className="text-muted-foreground mt-0.5">{provider.specialty}</p>

            {totalReviews > 0 && (
              <div className="mt-2">
                <StarRow rating={avgRating} count={totalReviews} />
              </div>
            )}

            <div className="flex flex-wrap gap-3 mt-3">
              {provider.address && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <MapPin size={13} />
                  {provider.address}
                </div>
              )}
              {provider.phone_number && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Phone size={13} />
                  {provider.phone_number}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Badges */}
        <div className="flex flex-wrap gap-2 mt-5 pt-4 border-t border-border">
          <div className="flex items-center gap-1.5 text-xs bg-muted px-3 py-1.5 rounded-full">
            <Shield size={13} className="text-primary" /> Licensed Provider
          </div>
          <div className="flex items-center gap-1.5 text-xs bg-muted px-3 py-1.5 rounded-full">
            <Award size={13} className="text-primary" /> {provider.specialty}
          </div>
          {provider.gender && (
            <div className="flex items-center gap-1.5 text-xs bg-muted px-3 py-1.5 rounded-full">
              <UserRound size={13} className="text-primary" /> {provider.gender}
            </div>
          )}
        </div>

        {/* Extra profile details */}
        {(provider.qualification || provider.experience != null || provider.years_of_experience != null || (provider.languages && provider.languages.length > 0)) && (
          <div className="mt-5 pt-4 border-t border-border space-y-3">
            {provider.qualification && (
              <div className="flex items-start gap-3">
                <Award size={15} className="text-muted-foreground mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">Qualification</p>
                  <p className="text-sm text-foreground">{provider.qualification}</p>
                </div>
              </div>
            )}
            {(provider.experience ?? provider.years_of_experience) != null && (
              <div className="flex items-start gap-3">
                <Award size={15} className="text-muted-foreground mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">Experience</p>
                  <p className="text-sm text-foreground">{provider.experience ?? provider.years_of_experience} Years</p>
                </div>
              </div>
            )}
            {provider.languages && provider.languages.length > 0 && (
              <div className="flex items-start gap-3">
                <UserRound size={15} className="text-muted-foreground mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">Languages</p>
                  <p className="text-sm text-foreground">{provider.languages.join(', ')}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* About */}
        {(provider.about ?? provider.bio) && (
          <div className="mt-4 pt-4 border-t border-border">
            <p className="text-xs font-semibold text-muted-foreground mb-1">About</p>
            <p className="text-sm text-foreground leading-relaxed">{provider.about ?? provider.bio}</p>
          </div>
        )}

        {/* Specializations */}
        {provider.specializations && provider.specializations.length > 0 && (
          <div className="mt-4 pt-4 border-t border-border">
            <p className="text-xs font-semibold text-muted-foreground mb-2">Specializations</p>
            <div className="flex flex-wrap gap-2">
              {provider.specializations.map((s, i) => (
                <span key={i} className="text-xs bg-muted text-muted-foreground px-2.5 py-1 rounded-full">{s}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Reviews */}
      {reviews.length > 0 && (
        <div className="bg-card border border-border border p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-foreground flex items-center gap-2">
              <Star size={16} className="text-yellow-400 fill-yellow-400" />
              Patient Reviews
            </h2>
            <span className="text-sm text-muted-foreground">{totalReviews} total</span>
          </div>
          <div className="space-y-3">
            <ProviderReviewsPanel
              reviews={displayedReviews}
              averageRating={avgRating}
              totalReviews={totalReviews}
              compact
            />
          </div>
          {reviews.length > 3 && (
            <button
              onClick={() => setShowAllReviews(!showAllReviews)}
              className="w-full mt-3 py-2 text-sm text-primary hover:underline"
            >
              {showAllReviews ? 'Show less' : `Show all ${reviews.length} reviews`}
            </button>
          )}
        </div>
      )}

      {/* Booking CTA */}
      {activeServices.length > 0 ? (
        <div className="bg-card border border-border border p-5">
          <h2 className="font-semibold text-foreground mb-3">Book an Appointment</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Select a service to book with {provider.name}
          </p>
          <div className="space-y-2">
            {activeServices.map((rate) => {
              const meta = SERVICE_META[rate.service_name]
              const Icon = meta?.icon ?? Stethoscope
              const isDermatology = rate.service_name === 'Dermatology'
              return (
                <Link
                  key={rate.id}
                  to={`/patient/book/${provider.id}?service=${encodeURIComponent(rate.service_name)}`}
                  className="flex items-center justify-between w-full p-3 rounded-xl border border-border hover:border-primary hover:bg-primary/5 transition-all group"
                >
                  <div className="flex items-center gap-3">
                    <div className={clsx('w-9 h-9 rounded-lg flex items-center justify-center', meta?.bg ?? 'bg-muted')}>
                      {isDermatology ? (
                        <img src={DERMATOLOGY_ICON} alt="" aria-hidden="true" className="h-5 w-5 object-contain" style={{ filter: 'brightness(0) invert(1)' }} />
                      ) : (
                        <Icon size={17} className={meta?.textColor ?? 'text-foreground'} />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">{rate.service_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {rate.base_rate !== null ? `₦${rate.base_rate.toLocaleString()}` : '—'}
                      </p>
                    </div>
                  </div>
                  <ChevronRight size={18} className="text-muted-foreground group-hover:text-primary transition-colors" />
                </Link>
              )
            })}
          </div>
        </div>
      ) : (
        <div className="bg-card border border-dashed border-border rounded-2xl p-6 text-center">
          <p className="text-sm text-muted-foreground">This provider has no active services at the moment.</p>
        </div>
      )}
    </div>
  )
}
